import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AddBoxIcon from '@mui/icons-material/AddBox';
import {
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import MoreHorizSharpIcon from '@mui/icons-material/MoreHorizSharp';
import { setGlobalMessage } from 'hooks/useGlobalMessage';
import ListPaginator from 'applications/signal/components/ListPaginator';
import MonitoringRecipientsManageModal from 'applications/signal/components/MonitoringRecipientsManageModal';
import { TableCellAttributes } from 'applications/signal/types/MuiRelated';
import { useDeleteSignalSetting, useToggleSignalSettingActive } from 'applications/signal/hooks/gqlHooks';
import { noticeTypeLabel, signalTypeLabel, thresholdLevelLabel } from 'applications/signal/utils/viewUtils';
import { AlertSnackbarProps } from "features/components/alertSnackbar";
import { SignalMonitoringSetting } from 'gql/graphql';

type SettingListProps = {
  rows: SignalMonitoringSetting[];
  page: number;
  setPage: (page: number) => void;
  rowsPerPage: number;
  setRowsPerPage: (rowsPerPage: number) => void;
  totalCount: number;
  setSnackBarMessage: (message: AlertSnackbarProps | null) => void;
};

const columns: TableCellAttributes[] = [
  { columnName: 'active', label: '有効', align: 'center' },
  { columnName: 'signal_type', label: 'シグナル<br>タイプ', minWidth: 100, align: 'center' },
  { columnName: 'notice_type', label: '通知<br>タイプ', minWidth: 85, align: 'center' },
  { columnName: 'name', label: '設定名', minWidth: 100, align: 'center' },
  { columnName: 'categories', label: 'カテゴリ', minWidth: 180, align: 'center' },
  { columnName: 'areas', label: 'エリア', minWidth: 100, align: 'center' },
  { columnName: 'channels', label: '業態', minWidth: 180, align: 'center' },
  { columnName: 'gender_ages', label: '性年代', minWidth: 120, align: 'center' },
  { columnName: 'shop_chains', label: 'チェーン', minWidth: 120, align: 'center' },
  { columnName: 'target_days', label: '累積日数', minWidth: 85, align: 'right' },
  { columnName: 'target_brand_name', label: '対象ブランド', minWidth: 200, align: 'center' },
  { columnName: 'threshold_level', label: '検知<br>感度', minWidth: 85, align: 'center' },
  { columnName: 'min_threshold', label: '最小<br>閾値', minWidth: 85, align: 'right' },
  { columnName: 'max_threshold', label: '最大<br>閾値', minWidth: 85, align: 'right' },
  { columnName: 'created_at', label: '作成日時', minWidth: 150, align: 'right' },
  { columnName: 'updated_at', label: '更新日時', minWidth: 150, align: 'right' },
];

function getField<T, K extends keyof T>(obj: T, key: K): T[K] {
  return obj[key];
}

function displayHtmlHelper(label: string) {
  return label.split('<br>').map((line, index) => (
    <span key={index}>
      {line}
      <br />
    </span>
  ));
}

export default function SettingList({
  rows, page, rowsPerPage, setPage, setRowsPerPage, totalCount, setSnackBarMessage
}: SettingListProps) {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const [selectedRow, setSelectedRow] = useState<SignalMonitoringSetting | null>(null);
  const [monitoringRecipientsManageModalOpen, setMonitoringRecipientsManageModalOpen] = useState(false);
  const { deleteSignalSetting } = useDeleteSignalSetting();
  const { toggleSignalSettingActive } = useToggleSignalSettingActive();

  const handleExtraMenuClick = (event: React.MouseEvent<HTMLElement>, row: SignalMonitoringSetting) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  const handleExtraMenuClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const handleNewSettingClick = () => {
    handleExtraMenuClose();
    navigate('/signal/condition_edit', {
      state: { formData: null }
    });
  };

  const handleRecipientsManageClick = (row: SignalMonitoringSetting | null) => {
    if (!row) return;
    setAnchorEl(null);
    setMonitoringRecipientsManageModalOpen(true);
  };

  const handleUpdateClick = (row: SignalMonitoringSetting | null) => {
    if (!row) return;
    handleExtraMenuClose();
    navigate("/signal/condition_edit", {
      state: { formData: row }
    });
  };

  const handleDelete = async () => {
    if (window.confirm('本当に削除しますか？')) {
      const id = selectedRow?.id;
      if (!id) return;
      const { data, error } = await deleteSignalSetting({ id });
      if (error) {
        setGlobalMessage({
          message: "Signal設定の削除に失敗しました。",
          severity: "error",
        });
      } else {
        const deletedIndex = rows.findIndex((row) => row.id === id);
        if (deletedIndex !== -1) {
          rows.splice(deletedIndex, 1); // クライアント側の状態更新
        }
        setGlobalMessage({
          message: "Signal設定を削除しました。",
          severity: "success",
        });
      }
      handleExtraMenuClose();
    }
  };

  const handleToggleActive = async (row: SignalMonitoringSetting) => {
    const { data, error } = await toggleSignalSettingActive({ id: row.id });
    if (error) {
      setGlobalMessage({
        message: "Signal設定の有効・無効切り替えに失敗しました。",
        severity: "error",
      });
    } else {
      rows.find((r) => r.id === row.id)!.active = !row.active; // クライアント側の状態更新
      setGlobalMessage({
        message: "Signal設定の有効・無効を切り替えました。",
        severity: "success",
      });
    }
  };

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', justifyContent: 'end', padding: 2 }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddBoxIcon />}
          onClick={handleNewSettingClick}
        >
          Signalを作成
        </Button>
      </Box>
      <TableContainer>
        <Table stickyHeader area-label="sticky table">
          <TableHead>
            <TableRow key="header">
              <TableCell />
              {columns.map((column, index) => (
                <TableCell
                  key={`${column.columnName}-${index}`}
                  align={column.headerAlign || "center"}
                  style={{ minWidth: column.minWidth }}
                >
                  {displayHtmlHelper(column.label)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((row) => {
                return (
                  <TableRow hover role="checkbox" tabIndex={-1} key={`${row.id}`}>
                    <TableCell align="right">
                      <IconButton onClick={(e) => handleExtraMenuClick(e, row)}>
                        <MoreHorizSharpIcon />
                      </IconButton>
                    </TableCell>
                    {columns.map((column, index) => {
                      const value = getField(row, column.columnName as keyof SignalMonitoringSetting);
                      const cellKey = `${column.columnName}Value-${index}`;
                      if (column.columnName === 'active') {
                        return (
                          <TableCell key={cellKey} align={column.align}>
                            <Switch
                              checked={value as boolean}
                              onChange={() => handleToggleActive(row)}
                              inputProps={{ 'aria-label': 'controlled' }}
                            />
                          </TableCell>
                        );
                      }
                      if (column.columnName === 'signal_type') {
                        return (
                          <TableCell key={cellKey} align={column.align}>
                            {signalTypeLabel(row.signal_type)}
                          </TableCell>
                        );
                      }
                      if (column.columnName === 'notice_type') {
                        return (
                          <TableCell key={cellKey} align={column.align}>
                            {noticeTypeLabel(row.notice_type)}
                          </TableCell>
                        );
                      }
                      if (column.columnName === 'threshold_level') {
                        return (
                          <TableCell key={cellKey} align={column.align}>
                            {row.threshold_level ? thresholdLevelLabel(row.threshold_level) : "-"}
                          </TableCell>
                        );
                      }
                      if (["categories", "areas", "channels", "gender_ages", "shop_chains"].includes(column.columnName)) {
                        return (
                          <TableCell key={cellKey} align={column.align}>
                            {
                              value && (value as string[]).length > 0 ? (
                                (value as string[]).map((item) => (
                                  <Box key={item} component="p" sx={{ margin: 0 }}>
                                    {item}
                                  </Box>
                                ))
                              ) : (
                                <Box component="p" sx={{ margin: 0 }}>
                                  -
                                </Box>
                              )
                            }
                          </TableCell>
                        );
                      }
                      return (
                        <TableCell key={cellKey} align={column.align}>
                          {value ? value : "-"}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
      <Box display="flex" justifyContent="flex-end">
        <ListPaginator
          totalCount={totalCount}
          page={page}
          rowsPerPage={rowsPerPage}
          setPage={setPage}
          setRowsPerPage={setRowsPerPage}
        />
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleExtraMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={() => handleRecipientsManageClick(selectedRow)}>受信者管理</MenuItem>
        <MenuItem onClick={() => handleUpdateClick(selectedRow)}>更新する</MenuItem>
        <MenuItem onClick={handleDelete}>削除する</MenuItem>
      </Menu>
      <MonitoringRecipientsManageModal
        monitoringSetting={selectedRow}
        open={monitoringRecipientsManageModalOpen}
        setOpen={setMonitoringRecipientsManageModalOpen}
        setSnackBarMessage={setSnackBarMessage}
        setSelectedRow={setSelectedRow}
      />
    </Paper>
  )
}
